/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { InsightComposer } from '../../pages/InsightComposer';
import { Login } from '@ink-ai/portal/insight-hub/pages/Login';
import { ThemeProvider } from '@ink-ai/portal/theme';
import App from '../../App';
import { useSelector } from 'react-redux';
import { Initializer } from '@ink-ai/portal/common/components/Initializer';
import { RootState } from '@ink-ai/portal/reducers';
import { Glossary } from '../../pages/GlossaryCollections';
import { GlossaryItemList } from '../../pages/GlossaryItemList';
import { ImportGlossary } from '../../pages/ImportGlossary';
import { Translation } from '../../pages/Translation';
import { ConnectorsSettings } from '../../pages/settings/ConnectorsSettings';
import { ImportConnector } from '../../pages/settings/ImportConnector';
import { ArticlesList } from '../../pages/ArticlesList';
import { TranslateDocumentTaskList } from '../../pages/TranslateDocumentTaskList';
import { ReferenceStore } from '../../pages/reference-store/ReferenceStoreList';
import { ImportReferenceStore } from '../../pages/reference-store/ImportReferenceStore';
import { ReferenceStoreDetail } from '../../pages/reference-store/ReferenceStoreDetail';
import { WritingInstructionsList } from '../../pages/WritingInstructionsList';
import { CreateWritingInstruction } from '../../pages/writing-instructions/CreateWritingInstruction';
import { EditWritingInstruction } from '../../pages/writing-instructions/EditWritingInstruction';
import { WritingRuleList } from '../../pages/WritingRuleList';
import { CreateWritingRule } from '../../pages/writing-rules/CreateWritingRule';
import { EditWritingRule } from '../../pages/writing-rules/EditWritingRule';
import { Settings } from '../../pages/settings';

const router = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/',
    element: <App />,
    children: [
      {
        path: '/',
        element: <Glossary />,
      },
      {
        path: '/articles',
        element: <ArticlesList />,
      },
      {
        path: '/translate-tasks',
        element: <TranslateDocumentTaskList />,
      },
      {
        path: '/medical-writer',
        element: <InsightComposer />,
      },
      {
        path: '/articles/new',
        element: <InsightComposer />,
      },
      {
        path: '/articles/:id/edit',
        element: <InsightComposer />,
      },
      {
        path: '/medical-translator',
        element: <Translation />,
      },
      {
        path: '/glossary',
        element: <Glossary />,
      },
      {
        path: '/glossary/:id',
        element: <GlossaryItemList />,
      },
      {
        path: '/glossary/import',
        element: <ImportGlossary />,
      },
      {
        path: '/reference-store',
        element: <ReferenceStore />,
      },
      {
        path: '/reference-store/:id',
        element: <ReferenceStoreDetail />,
      },
      {
        path: '/reference-store/import',
        element: <ImportReferenceStore />,
      },
      {
        path: '/writing-instruction',
        element: <WritingInstructionsList />,
      },
      {
        path: '/writing-instruction/new',
        element: <CreateWritingInstruction />,
      },
      {
        path: '/writing-instruction/:id/edit',
        element: <EditWritingInstruction />,
      },
      {
        path: '/writing-rule',
        element: <WritingRuleList />,
      },
      {
        path: '/writing-rule/:id/edit',
        element: <EditWritingRule />,
      },
      {
        path: '/writing-rule/new',
        element: <CreateWritingRule />,
      },
      {
        path: '/settings/connectors',
        element: <ConnectorsSettings />,
      },
      {
        path: '/settings/connector/import',
        element: <ImportConnector />,
      },
      {
        path: '/settings',
        element: <Settings />,
      },
    ],
  },
]);

export const AppRouter = () => {
  const app = useSelector((state: RootState) => state.app);
  return (
    <>
      {app.isInitializing ? (
        <Initializer />
      ) : (
        <ThemeProvider>
          <RouterProvider router={router} />
        </ThemeProvider>
      )}
    </>
  );
};
